// add on enter to editor options
import { Content, EditorOptions } from '@tiptap/core';
import { Editor, useEditor as useEditorBase } from '@tiptap/react';
import { DependencyList, useCallback, useRef, useState } from 'react';
import { useFileUploadContext } from 'src/provider/FileUploadContext';
import { AssetsRequests, IFile } from 'src/requests/assets';
import { EditorExtensionsConfig } from './private/types/EditorExtensionsConfig';
import { getEditorPrevText } from './private/utils/getEditorPrevText';
import { TiptapEditorProps } from './props';

declare module '@tiptap/core' {
  export interface EditorOptions {
    onEnter(): void;
    extensionConfig: EditorExtensionsConfig;
    uploadFile?(file: File): Promise<IFile>;
    downloadFile: typeof AssetsRequests.getPrivateFile;
  }
}

type Options = Partial<EditorOptions>;

export const useEditor = (options: Options, deps?: DependencyList) => {
  const valueRef = useRef<Content | undefined>(options.content);
  const [editorLoaded, setEditorLoaded] = useState<boolean>(false);
  const { control } = useFileUploadContext();

  const handleUpload = useCallback(
    async (file: File) => {
      const [fileUpload] = control.addFiles([file]);
      return AssetsRequests.uploadPrivateFile(
        fileUpload.file,
        (progress, speed) => {
          control.updateFile({
            ...fileUpload,
            progress,
            speed,
          });
        },
      ).then((response) => {
        control.removeFiles([fileUpload]);
        return response;
      });
    },
    [control],
  );

  const editor = useEditorBase(
    {
      ...options,
      editorProps: TiptapEditorProps,
      uploadFile: handleUpload,
      downloadFile: options.downloadFile || AssetsRequests.getPrivateFile,
      onUpdate: (e) => {
        const { selection } = e.editor.state;
        const lastTwo = getEditorPrevText(e.editor, {
          chars: 2,
        });
        if (lastTwo === '++') {
          e.editor.commands.deleteRange({
            from: selection.from - 2,
            to: selection.from,
          });
        } else {
          valueRef.current = e.editor.getHTML();
        }
      },
      onCreate: () => setEditorLoaded(true),
    },
    deps,
  );

  return {
    currentValue: valueRef.current,
    editor: editor as Editor,
    editorLoaded,
  };
};
